/**
 * FAQ Searchable Component
 * Modern FAQ section with search, category filtering, and smooth animations
 */

class FAQSearchable extends HTMLElement {
  constructor() {
    super();
    this.searchInput = null;
    this.clearButton = null;
    this.resultCount = null;
    this.resultNumber = null;
    this.categoryButtons = null;
    this.items = null;
    this.categoryGroups = null;
    this.noResults = null;
    this.currentCategory = 'all';
    this.searchTimeout = null;
  }

  connectedCallback() {
    this.initElements();
    this.bindEvents();
    this.initAccordions();
  }

  initElements() {
    this.searchInput = this.querySelector('.faq-searchable__search-input');
    this.clearButton = this.querySelector('.faq-searchable__search-clear');
    this.resultCount = this.querySelector('.faq-searchable__result-count');
    this.resultNumber = this.querySelector('.faq-searchable__result-number');
    this.categoryButtons = this.querySelectorAll('.faq-searchable__category-btn');
    this.items = this.querySelectorAll('.faq-searchable__item');
    this.categoryGroups = this.querySelectorAll('.faq-searchable__category-group');
    this.noResults = this.querySelector('.faq-searchable__no-results');
  }

  bindEvents() {
    // Search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.handleSearch());
      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.clearSearch();
        }
      });
    }

    // Clear button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearSearch());
    }

    // Category buttons
    this.categoryButtons.forEach(button => {
      button.addEventListener('click', () => this.handleCategoryClick(button));
    });
  }

  initAccordions() {
    const details = this.querySelectorAll('.faq-searchable__details');

    details.forEach(detail => {
      detail.addEventListener('toggle', (e) => {
        if (detail.open) {
          // Smooth scroll into view if needed
          setTimeout(() => {
            const rect = detail.getBoundingClientRect();
            const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0');

            if (rect.top < headerHeight + 20) {
              window.scrollBy({
                top: rect.top - headerHeight - 20,
                behavior: 'smooth'
              });
            }
          }, 100);
        }
      });
    });
  }

  handleSearch() {
    // Debounce search
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.performSearch(), 150);
  }

  performSearch() {
    const query = this.searchInput.value.trim().toLowerCase();

    // Toggle clear button
    this.clearButton.hidden = query.length === 0;

    if (query.length === 0) {
      this.showAllItems();
      this.resultCount.hidden = true;
      return;
    }

    // Normalize search terms for Hungarian
    const searchTerms = this.normalizeHungarian(query).split(/\s+/).filter(t => t.length > 0);
    let matchCount = 0;

    this.items.forEach(item => {
      const questionText = this.normalizeHungarian(item.dataset.question || '');
      const answerText = this.normalizeHungarian(item.dataset.answer || '');
      const fullText = questionText + ' ' + answerText;

      // Check if all search terms match
      const matches = searchTerms.every(term => fullText.includes(term));

      // Also check category filter
      const categoryMatch = this.currentCategory === 'all' ||
                           item.dataset.category === this.currentCategory;

      if (matches && categoryMatch) {
        item.hidden = false;
        item.classList.add('highlight');
        setTimeout(() => item.classList.remove('highlight'), 600);
        matchCount++;
      } else {
        item.hidden = true;
      }
    });

    // Update result count
    this.resultNumber.textContent = matchCount;
    this.resultCount.hidden = false;

    // Show/hide no results message
    this.noResults.hidden = matchCount > 0;

    // Update category group visibility
    this.updateCategoryGroupVisibility();
  }

  normalizeHungarian(text) {
    // Normalize Hungarian accented characters for better search
    return text
      .toLowerCase()
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ó|ő|ö/g, 'o')
      .replace(/ú|ű|ü/g, 'u');
  }

  clearSearch() {
    this.searchInput.value = '';
    this.clearButton.hidden = true;
    this.resultCount.hidden = true;
    this.showAllItems();
    this.searchInput.focus();
  }

  showAllItems() {
    this.items.forEach(item => {
      const categoryMatch = this.currentCategory === 'all' ||
                           item.dataset.category === this.currentCategory;
      item.hidden = !categoryMatch;
    });

    this.noResults.hidden = true;
    this.updateCategoryGroupVisibility();
  }

  handleCategoryClick(button) {
    // Update active state
    this.categoryButtons.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    // Update current category
    this.currentCategory = button.dataset.category;

    // Re-run search if there's a query, otherwise just filter by category
    if (this.searchInput.value.trim()) {
      this.performSearch();
    } else {
      this.filterByCategory();
    }
  }

  filterByCategory() {
    let visibleCount = 0;

    this.items.forEach(item => {
      const categoryMatch = this.currentCategory === 'all' ||
                           item.dataset.category === this.currentCategory;
      item.hidden = !categoryMatch;

      if (categoryMatch) {
        visibleCount++;
        // Add staggered animation
        item.style.animation = 'none';
        item.offsetHeight; // Trigger reflow
        item.style.animation = '';
      }
    });

    this.updateCategoryGroupVisibility();
    this.noResults.hidden = visibleCount > 0;
  }

  updateCategoryGroupVisibility() {
    this.categoryGroups.forEach(group => {
      const categoryHandle = group.dataset.categoryGroup;
      const visibleItems = group.querySelectorAll('.faq-searchable__item:not([hidden])');

      // If filtering by category and this isn't the selected one, hide the group
      if (this.currentCategory !== 'all' && this.currentCategory !== categoryHandle) {
        group.hidden = true;
      } else {
        // Otherwise, show if there are visible items
        group.hidden = visibleItems.length === 0;
      }
    });
  }
}

// Register custom element
if (!customElements.get('faq-searchable')) {
  customElements.define('faq-searchable', FAQSearchable);
}
